import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { authApi } from "../../api/authApi";

const LINGUE = [
  { value: "it", label: "Italiano" },
  { value: "en", label: "English" },
];

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
}

export default function ProfilePage() {
  const { user, token, setToken } = useAuth() as {
    user?: { id: string; fullName: string; email: string; role: string } | null;
    token: string | null;
    setToken: (t: string | null, u?: { id: string; email: string; fullName: string; role: string } | null) => void;
  };

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [locale, setLocale] = useState("it");
  const [address, setAddress] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!token) return;
    authApi
      .getProfile(token)
      .then((p) => {
        setFullName(p.fullName ?? "");
        setEmail(p.email ?? "");
        setLocale(p.locale ?? "it");
        setAddress(p.address ?? "");
      })
      .catch((e) => setError(e?.message ?? "Errore caricamento profilo"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!token) return;

    if (newPassword) {
      if (newPassword.length < 6) {
        setError("La nuova password deve essere di almeno 6 caratteri.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setError("La nuova password e la conferma non coincidono.");
        return;
      }
      if (!oldPassword) {
        setError("Inserisci la vecchia password per cambiarla.");
        return;
      }
    }

    setSaving(true);
    try {
      const updated = await authApi.updateProfile(token, {
        fullName: fullName.trim(),
        email: email.trim(),
        locale: locale || "it",
        address: address.trim() || undefined,
        oldPassword: oldPassword || undefined,
        newPassword: newPassword || undefined,
      });
      setToken(token, {
        id: updated.id,
        email: updated.email,
        fullName: updated.fullName,
        role: updated.role,
      });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess("Profilo aggiornato con successo.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Errore aggiornamento profilo");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="profile-page-wrapper">
        <div className="alert alert-info">Accedi per vedere il profilo.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profile-page-wrapper">
        <div className="text-muted d-flex align-items-center gap-2">
          <div className="spinner-border spinner-border-sm text-primary" />
          Caricamento profilo…
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page-wrapper">
      <div className="profile-card card">
        <div className="profile-card-header">
          <div className="d-flex align-items-center gap-3">
            <div className="profile-avatar">
              {getInitials(fullName || user?.fullName || "U")}
            </div>
            <div>
              <h1 className="h5 mb-0 fw-bold">{fullName || user?.fullName || "Utente"}</h1>
              <p className="mb-0 opacity-75 small">{email || user?.email}</p>
              <span className="badge bg-light text-dark mt-1">{user?.role ?? "OPERATOR"}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="profile-form-body">
          <div className="profile-form-grid">
            <div className="profile-section">
              <h6 className="profile-section-title">
                <i className="bi bi-person me-2" />
                Informazioni personali
              </h6>
              <div className="auth-form">
                <div className="mb-3">
                  <label htmlFor="profile-fullName" className="form-label">Nome</label>
                  <input
                    id="profile-fullName"
                    type="text"
                    className="form-control"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="profile-email" className="form-label">Email</label>
                  <input
                    id="profile-email"
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@esempio.it"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="profile-locale" className="form-label">Lingua</label>
                  <select
                    id="profile-locale"
                    className="form-select"
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                  >
                    {LINGUE.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-0">
                  <label htmlFor="profile-address" className="form-label">Indirizzo</label>
                  <input
                    id="profile-address"
                    type="text"
                    className="form-control"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Via, numero civico, CAP, città"
                  />
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h6 className="profile-section-title">
                <i className="bi bi-key me-2" />
                Cambia password
              </h6>
              <p className="small text-muted mb-2">
                Lascia vuoto se non vuoi modificare la password.
              </p>
              <div className="auth-form">
                <div className="mb-3">
                  <label htmlFor="profile-oldPassword" className="form-label">Vecchia password</label>
                  <input
                    id="profile-oldPassword"
                    type="password"
                    className="form-control"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="profile-newPassword" className="form-label">Nuova password</label>
                  <input
                    id="profile-newPassword"
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </div>
                <div className="mb-0">
                  <label htmlFor="profile-confirmPassword" className="form-label">Conferma nuova password</label>
                  <input
                    id="profile-confirmPassword"
                    type="password"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="profile-form-footer">
            {error && (
              <div className="alert alert-danger auth-alert mb-2 py-2" role="alert">
                <i className="bi bi-exclamation-circle me-2" />
                {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success auth-alert mb-2 py-2" role="alert">
                <i className="bi bi-check-circle me-2" />
                {success}
              </div>
            )}
            <button
              type="submit"
              className="btn auth-btn-primary"
              style={{ padding: "0.5rem 1.25rem", fontSize: "0.9rem" }}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Salvataggio…
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2" />
                  Salva modifiche
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
